// services/school-ui/src/pages/chat/parts/LiveAgentActivity.tsx
//
// Renders the live AG-UI run activity in the teacher right-rail "动态"
// panel. Data sources:
//   1. progressStore — nova-os's rich progress (nova.phase / nova.plan /
//      nova.step / nova.skill_*), tapped at the transport layer
//      (HttpAgentWithContext.subscribe) because the @assistant-ui/react
//      aggregator drops CUSTOM events from ThreadState. nova.plan carries the
//      planner's Chinese step descriptions; nova.step drives per-step status;
//      nova.skill_completed carries elapsed_ms.
//   2. useThread — isRunning + tool-call message parts (fallback for runs
//      that emit no nova.* progress).
//
// NEVER fabricates status — only renders signals that actually fired.

import { useEffect, useState, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { useThread } from "@assistant-ui/react";
import type { ToolCallMessagePart } from "@assistant-ui/react";
import {
  getProgressSnapshot,
  subscribeProgress,
  type ProgressState,
  type StepStatus,
} from "../progressStore";

interface ToolRow {
  id: string;
  name: string;
  result?: string;
  done: boolean;
}

function fmtSecs(ms: number): string {
  return `${(ms / 1000).toFixed(1)}s`;
}

function humanize(s: string): string {
  return s.replace(/[_-]+/g, " ").trim();
}

function dotClass(status: StepStatus): string {
  if (status === "done") return "done";
  if (status === "error") return "error";
  if (status === "started") return "running";
  return "pending";
}

export function LiveAgentActivity(): JSX.Element {
  const { t } = useTranslation();
  // nova.skill_* events carry internal identifiers (report_generation /
  // skill_report) with no localized label — map known ones through i18n,
  // fall back to the humanized identifier for anything unmapped.
  const capLabel = (key: string): string =>
    key ? t(`chat.activity.capability.${key}`, { defaultValue: humanize(key) }) : "";
  const progress: ProgressState = useSyncExternalStore(
    subscribeProgress,
    getProgressSnapshot,
    getProgressSnapshot,
  );
  const thread = useThread({ optional: true });
  const [collapsed, setCollapsed] = useState(false);

  const isRunning = progress.running || Boolean(thread?.isRunning);

  // 1s tick to drive the live running-timer (off when idle to avoid churn).
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!isRunning) return;
    const h = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(h);
  }, [isRunning]);

  // Tool-call parts from the latest assistant message (fallback signal).
  const toolRows: ToolRow[] = [];
  if (thread) {
    for (let i = thread.messages.length - 1; i >= 0; i--) {
      const msg = thread.messages[i];
      if (!msg || msg.role !== "assistant") continue;
      for (const part of msg.content) {
        if (part.type === "tool-call") {
          const tc = part as ToolCallMessagePart;
          toolRows.push({
            id: tc.toolCallId,
            name: tc.toolName,
            result: tc.result !== undefined ? String(tc.result) : undefined,
            done: tc.result !== undefined || !thread.isRunning,
          });
        }
      }
      break;
    }
  }

  const { steps, skills } = progress;
  const hasSteps = steps.length > 0;
  const hasSkills = skills.length > 0;
  const isIdle =
    !isRunning && !hasSteps && !hasSkills && toolRows.length === 0;

  if (isIdle) {
    return (
      <section className="teacher-agent-activity teacher-agent-activity-idle">
        <h4>{t("chat.activity.heading")}</h4>
        <p>{t("chat.activity.idle")}</p>
      </section>
    );
  }

  const total = hasSteps ? steps.length : skills.length;
  const doneCount = hasSteps
    ? steps.filter((s) => s.status === "done").length
    : skills.filter((s) => s.status === "done").length;

  // Coarse phase only when we have no detailed nova.* steps/skills.
  const phaseLabel =
    isRunning && !hasSteps && !hasSkills
      ? toolRows.length === 0
        ? t("chat.activity.phase.planning")
        : t("chat.activity.phase.synthesizing")
      : null;

  return (
    <section className="teacher-agent-activity">
      <button
        type="button"
        className="activity-head school-focus-ring"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
      >
        <h4>{t("chat.activity.heading")}</h4>
        {total > 0 && (
          <span className="count">
            {doneCount}/{total}
          </span>
        )}
        <span className={`chev ${collapsed ? "is-collapsed" : ""}`} aria-hidden>
          ⌄
        </span>
      </button>

      {!collapsed && (
        <>
          {phaseLabel && <p className="phase">{phaseLabel}</p>}

          {hasSteps && (
            <ol className="agent-steps">
              {/* Planning row — by the time plan steps exist, planning is
                  done. LibreChat-fork parity (#29). */}
              <li className="done">
                <span className="dot" aria-hidden />
                <span className="label">{t("chat.activity.planningStep")}</span>
              </li>
              {steps.map((s, i) => {
                const live =
                  s.status === "started" && s.startedAt
                    ? Date.now() - s.startedAt
                    : undefined;
                const elapsed = s.elapsedMs ?? live;
                // Skill dispatches whose capability matches nest under the
                // step they execute — the same correlation used for
                // elapsedMs. Skills matching no step stay in the flat
                // fallback list (not rendered when steps exist; rare).
                const subSkills = skills.filter(
                  (sk) => sk.capability === s.capability,
                );
                return (
                  <li key={s.id} className={dotClass(s.status)}>
                    {s.status === "pending" ? (
                      <span className="ordinal" aria-hidden>
                        {i + 1}
                      </span>
                    ) : (
                      <span className="dot" aria-hidden />
                    )}
                    <span className="label">
                      {s.description || humanize(s.capability) || s.id}
                    </span>
                    {elapsed !== undefined && (
                      <span className="meta">{fmtSecs(elapsed)}</span>
                    )}
                    {subSkills.length > 0 && s.status !== "pending" && (
                      <ul className="substeps">
                        {subSkills.map((sk) => (
                          <li
                            key={sk.key}
                            className={
                              sk.status === "done"
                                ? "done"
                                : sk.status === "failed"
                                  ? "error"
                                  : "running"
                            }
                          >
                            <span className="dot" aria-hidden />
                            <span className="label">
                              {capLabel(sk.capability || sk.skill)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ol>
          )}

          {!hasSteps && hasSkills && (
            <ul className="agent-skills">
              {skills.map((sk) => (
                <li
                  key={sk.key}
                  className={
                    sk.status === "done"
                      ? "done"
                      : sk.status === "failed"
                        ? "error"
                        : "running"
                  }
                >
                  <span className="dot" aria-hidden />
                  <span className="label">
                    {capLabel(sk.capability || sk.skill)}
                  </span>
                  {typeof sk.elapsedMs === "number" && sk.status !== "running" && (
                    <span className="meta">{fmtSecs(sk.elapsedMs)}</span>
                  )}
                </li>
              ))}
            </ul>
          )}

          {toolRows.length > 0 && (
            <ul className="tools">
              {toolRows.map((tc) => (
                <li key={tc.id} className={tc.done ? "done" : "running"}>
                  <span className="name">{tc.name}</span>
                  <span className="badge">
                    {tc.done
                      ? t("chat.activity.toolCall.done")
                      : t("chat.activity.toolCall.started")}
                  </span>
                  {tc.result && (
                    <details>
                      <summary>{t("chat.activity.toolCall.resultLabel")}</summary>
                      <pre>{tc.result}</pre>
                    </details>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
